/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  todos: {
    all: ["todos"] as const,
    list: <T extends object>(options: T) => ["todos", "list", options] as const,
    /** Variante paginada de `list` (límite incremental de la vista). */
    listPaged: <T extends object>(options: T, limit: number) =>
      ["todos", "list", options, limit] as const,
    /** Conteo de pendientes de una lista (badge). Derivada de `list` → se invalida con su prefijo. */
    listCount: <T extends object>(options: T) => ["todos", "list", options, "count"] as const,
    /**
     * Detalle de una tarea. Clave AGNÓSTICA de cuenta: el scoping por cuenta lo
     * inyecta de forma transparente `createAccountPowerSyncQuery` (prefijo
     * `["acct", userId, …]`). NO añadir `userId` aquí — usa el wrapper.
     */
    detail: (id: string | undefined) => ["todos", "detail", id] as const,
    /** Resolución list_id de un todo para el redirect de /todo/:id. */
    detailRedirect: (id: string | undefined) => ["todos", "detail-redirect", id] as const,
    stats: (userId: string | undefined) => ["todos", "stats", userId] as const,
    remindersPool: () => ["todos", "reminders-pool"] as const,
  },
  // 🎯 FamiDex — catálogo global [H] y colección privada [L] (Wave 0).
  catalog: {
    all: ["catalog"] as const,
    /** Catálogo de series (base del progreso por serie). Global, sin userId. */
    series: () => ["catalog", "series"] as const,
  },
  collection: {
    all: ["collection"] as const,
    /** Ítems vivos de la colección del usuario. Aislada por cuenta. */
    items: (userId: string | undefined) => ["collection", "items", userId] as const,
    /** Filas de progreso poseído-por-serie del usuario. */
    progress: (userId: string | undefined) => ["collection", "progress", userId] as const,
  },
  // 🧑‍🎨 FamiDex — perfil del coleccionista (Wave 1 · W1-PROFILE). Aislado por cuenta.
  collectorProfile: {
    all: ["collectorProfile"] as const,
    /** Ajustes del perfil (privacidad + marco + bio). */
    settings: (userId: string | undefined) => ["collectorProfile", "settings", userId] as const,
    /** Destacados (máx. 5) resueltos contra el catálogo. */
    featured: (userId: string | undefined) => ["collectorProfile", "featured", userId] as const,
    /** Favoritos del usuario. */
    favorites: (userId: string | undefined) => ["collectorProfile", "favorites", userId] as const,
    /** Etiquetas del usuario. */
    tags: (userId: string | undefined) => ["collectorProfile", "tags", userId] as const,
    /** Nº de seguidores (grafo social, sólo lectura). */
    followers: (userId: string | undefined) => ["collectorProfile", "followers", userId] as const,
    /** Nº de seguidos (grafo social, sólo lectura). */
    following: (userId: string | undefined) => ["collectorProfile", "following", userId] as const,
  },
  // 🎮 FamiDex — gamificación (rangos/XP/stickers, Wave 1 agente E).
  gamification: {
    all: ["gamification"] as const,
    /** Ledger de XP del usuario (base de la XP acumulada y el rango). */
    xpLedger: (userId: string | undefined) => ["gamification", "xp-ledger", userId] as const,
    /** Catálogo de paquetes de stickers (global, sin userId). */
    stickerPacks: () => ["gamification", "sticker-packs"] as const,
    /** Stickers poseídos por el usuario. */
    userStickers: (userId: string | undefined) =>
      ["gamification", "user-stickers", userId] as const,
    /** Conteo poseído-por-paquete del usuario (progreso por paquete). */
    packProgress: (userId: string | undefined) =>
      ["gamification", "pack-progress", userId] as const,
  },
  // 👥 FamiDex — grafo social + chat/DM + notificaciones (Wave 1, agente D).
  social: {
    all: ["social"] as const,
    /** Seguidores (soy followee). Aislado por cuenta. */
    followers: (userId: string | undefined) => ["social", "followers", userId] as const,
    /** Seguidos (soy follower). */
    following: (userId: string | undefined) => ["social", "following", userId] as const,
    /** Arista concreta follower->followee (estado del boton seguir). */
    edge: (followerId: string | undefined, followeeId: string | undefined) =>
      ["social", "edge", followerId, followeeId] as const,
    /** Centro de notificaciones del usuario. */
    notifications: (userId: string | undefined) => ["social", "notifications", userId] as const,
  },
  chat: {
    all: ["chat"] as const,
    /** Bandeja de hilos donde participo. */
    threads: (userId: string | undefined) => ["chat", "threads", userId] as const,
    /** Cabecera de un hilo por id. */
    thread: (threadId: string | undefined) => ["chat", "thread", threadId] as const,
    /** Mensajes de un hilo. */
    messages: (threadId: string | undefined) => ["chat", "messages", threadId] as const,
  },
  // FamiDex Wave 1-F — economía de Bellotas (server-authoritative, leído por sync).
  economy: {
    all: ["economy"] as const,
    /** Wallet (saldo) del usuario. Aislado por cuenta. */
    wallet: (userId: string | undefined) => ["economy", "wallet", userId] as const,
    /** Ledger de transacciones del usuario. */
    transactions: (userId: string | undefined) => ["economy", "transactions", userId] as const,
    /** Tienda (packs/marcos/stickers). Global, sin userId. */
    shop: (kind?: string) => ["economy", "shop", kind] as const,
    /** Canjes del usuario (marcos/stickers obtenidos). */
    redemptions: (userId: string | undefined) => ["economy", "redemptions", userId] as const,
    /** Reclamaciones de la recompensa diaria del usuario. */
    dailyClaims: (userId: string | undefined) => ["economy", "dailyClaims", userId] as const,
  },
  // 📣 FamiDex — comunidad (feed/posts/likes/comentarios, Wave 1-C).
  community: {
    all: ["community"] as const,
    /** Feed cronológico público (descubrimiento). Global, sin userId. */
    feed: () => ["community", "feed"] as const,
    /** Likes del feed (para contadores y estado likeado del usuario). */
    feedLikes: () => ["community", "feed-likes"] as const,
    /** Detalle de una publicación por id. */
    post: (postId: string | undefined) => ["community", "post", postId] as const,
    /** Likes de una publicación concreta. */
    postLikes: (postId: string | undefined) => ["community", "post-likes", postId] as const,
    /** Comentarios de una publicación. */
    comments: (postId: string | undefined) => ["community", "comments", postId] as const,
    /** Galería comunitaria de un producto (publicaciones etiquetadas). */
    productGallery: (productId: string | undefined) =>
      ["community", "product-gallery", productId] as const,
    /** Publicaciones de un perfil. */
    userPosts: (userId: string | undefined) => ["community", "user-posts", userId] as const,
    /** Reporte del usuario sobre una publicación (idempotencia). */
    report: (postId: string | undefined, userId: string | undefined) =>
      ["community", "report", postId, userId] as const,
  },
  activity: {
    all: ["activity"] as const,
    /** Feed local (activity_recent). Incluye userId: aísla caché entre cuentas. */
    feed: (userId: string | undefined) => ["activity", "feed", userId] as const,
    /** Historial local de una entidad concreta. */
    entity: (userId: string | undefined, entityType: string, entityId: string | undefined) =>
      ["activity", "entity", userId, entityType, entityId] as const,
  },
  trash: {
    all: ["trash"] as const,
    lists: (userId: string | undefined) => ["trash", "lists", userId] as const,
    todos: (userId: string | undefined) => ["trash", "todos", userId] as const,
  },
  search: {
    all: ["search"] as const,
    /** Resultados de búsqueda. Scoped por cuenta vía `createAccountPowerSyncQuery`. */
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Stats de cola de subida (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** ¿Hay datos locales? (botón de limpieza en profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  lists: {
    all: ["lists"] as const,
    /** Organización (sharing) asociada a una lista. Scoped vía wrapper de cuenta. */
    organization: (listId: string | undefined) => ["lists", "organization", listId] as const,
    /** All lists with theme + group metadata for a user (sidebar / route). */
    customLists: (userId: string | undefined) => ["lists", "customLists", userId] as const,
    /** List groups for a user. */
    groups: (userId: string | undefined) => ["lists", "groups", userId] as const,
    /** Single list detail by id. Scoped por cuenta vía `createAccountPowerSyncQuery`. */
    detail: (listId: string | undefined) => ["lists", "detail", listId] as const,
    /** Pending todo count per list_id (GROUP BY). */
    todoCounts: (userId: string | undefined) => ["lists", "todoCounts", userId] as const,
    /** Count of lists owned by user (profile stats). */
    count: (userId: string | undefined) => ["lists", "count", userId] as const,
    withCounts: (userId: string | undefined, limit?: number) =>
      ["lists", "withCounts", userId, limit] as const,
  },
  myDay: {
    all: ["myDay"] as const,
    /** Tareas de "Mi día". Scoped por cuenta vía `createAccountPowerSyncQuery`. */
    todos: (dateKey: string) => ["myDay", "todos", dateKey] as const,
    /** Sugerencias de "Mi día". Scoped por cuenta vía wrapper. */
    suggestions: (dateKey: string) => ["myDay", "suggestions", dateKey] as const,
  },
  smartViews: {
    counts: (userId: string | undefined) => ["smartViews", "counts", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Catálogo completo de temas (getThemesQuery sin paginar; profile + list-dialog COMPARTEN caché). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  subtasks: {
    all: ["subtasks"] as const,
    /** Subtareas de una tarea. Scoped por cuenta vía `createAccountPowerSyncQuery`. */
    byTodo: (todoId: string | undefined) => ["subtasks", todoId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    /** Adjuntos de una tarea. Scoped por cuenta vía `createAccountPowerSyncQuery`. */
    byTodo: (todoId: string) => ["attachments", todoId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
